import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { planPricingService, PricingTier, PlanPricing, PlanFeature, PlanMetadata } from '../../services/planPricingService';
import { Timestamp } from 'firebase/firestore';

const AdminPlanPricing: React.FC = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isDark } = useAdminTheme();

  const [planPricing, setPlanPricing] = useState<PlanPricing | null>(null);
  const [originalPricing, setOriginalPricing] = useState<PlanPricing | null>(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [activeTab, setActiveTab] = useState<'starter' | 'pro' | 'business'>('pro');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditType, setBulkEditType] = useState<'percentage' | 'fixed'>('percentage');
  const [bulkEditValue, setBulkEditValue] = useState(0);
  const [bulkEditPlan, setBulkEditPlan] = useState<'pro' | 'business'>('pro');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const historyRef = useRef<PlanPricing[]>([]);
  const historyIndexRef = useRef(-1);

  useEffect(() => {
    const loadPlanPricing = async () => {
      setIsLoadingPricing(true);
      try {
        const pricing = await planPricingService.getPlanPricing();
        setPlanPricing(pricing);
        setOriginalPricing(JSON.parse(JSON.stringify(pricing)));
        historyRef.current = [JSON.parse(JSON.stringify(pricing))];
        historyIndexRef.current = 0;
      } catch (error) {
        showError('Failed to load plan pricing');
      } finally {
        setIsLoadingPricing(false);
      }
    };
    loadPlanPricing();
  }, [showError]);

  const validatePricing = useCallback((plan: 'pro' | 'business', tiers: PricingTier[]): string[] => {
    const errors: string[] = [];
    if (tiers.length === 0) {
      errors.push(`${plan} plan must have at least one pricing tier`);
      return errors;
    }
    const sortedTiers = [...tiers].sort((a, b) => a.subscribers - b.subscribers);
    for (let i = 1; i < sortedTiers.length; i++) {
      if (sortedTiers[i].subscribers <= sortedTiers[i - 1].subscribers) {
        errors.push(`${plan} plan tiers must be in ascending order`);
        break;
      }
    }
    return errors;
  }, []);

  useEffect(() => {
    if (!planPricing) return;
    const errors: string[] = [];
    errors.push(...validatePricing('pro', planPricing.pro.tiers));
    errors.push(...validatePricing('business', planPricing.business.tiers));

    // Only update if errors actually changed to prevent loop
    setValidationErrors(prev => {
      if (JSON.stringify(prev) === JSON.stringify(errors)) return prev;
      return errors;
    });
  }, [planPricing, validatePricing]);

  const saveToHistory = (pricing: PlanPricing) => {
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(JSON.parse(JSON.stringify(pricing)));
    historyIndexRef.current = historyRef.current.length - 1;
    if (historyRef.current.length > 50) { historyRef.current.shift(); historyIndexRef.current--; }
  };

  const handlePricingTierChange = (plan: 'pro' | 'business', index: number, field: 'subscribers' | 'price', value: number) => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    newPricing[plan].tiers[index][field] = value;
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleAddPricingTier = (plan: 'pro' | 'business') => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    const tiers = newPricing[plan].tiers;
    const lastTier = tiers[tiers.length - 1];
    const newTier: PricingTier = {
      subscribers: lastTier ? lastTier.subscribers + 1000 : 1000,
      price: lastTier ? lastTier.price + 10 : 10
    };
    newPricing[plan].tiers.push(newTier);
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleRemovePricingTier = (plan: 'pro' | 'business', index: number) => {
    if (!planPricing) return;
    if (planPricing[plan].tiers.length <= 1) { showError('At least one tier is required'); return; }
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    newPricing[plan].tiers.splice(index, 1);
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleBulkEdit = () => {
    if (!planPricing || bulkEditValue === 0) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    newPricing[bulkEditPlan].tiers = newPricing[bulkEditPlan].tiers.map((tier: PricingTier) => ({
      ...tier,
      price: bulkEditType === 'percentage'
        ? Math.round((tier.price * (1 + bulkEditValue / 100)) * 100) / 100
        : Math.round((tier.price + bulkEditValue) * 100) / 100
    }));
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
    setShowBulkEdit(false);
    setBulkEditValue(0);
    showSuccess('Bulk update applied');
  };

  const handleMetadataChange = (plan: 'starter' | 'pro' | 'business', field: string, value: any) => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));

    if (field.includes('.')) {
      const parts = field.split('.');
      let current = newPricing[plan].metadata;
      for (let i = 0; i < parts.length - 1; i++) {
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      newPricing[plan].metadata[field] = value;

      if (field === 'isPopular' && value === true) {
        if (plan !== 'starter') newPricing.starter.metadata.isPopular = false;
        if (plan !== 'pro') newPricing.pro.metadata.isPopular = false;
        if (plan !== 'business') newPricing.business.metadata.isPopular = false;
      }
    }

    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleFeatureChange = (plan: 'starter' | 'pro' | 'business', featureId: string, field: 'included' | 'value' | 'name', value: any) => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    const features = newPricing[plan].metadata.features;
    const idx = features.findIndex((f: PlanFeature) => f.id === featureId);
    if (idx > -1) {
      features[idx][field] = value;
    }
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleAddFeature = (plan: 'starter' | 'pro' | 'business') => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    const newFeature: PlanFeature = {
      id: `custom_${Date.now()}`,
      name: 'New Feature',
      included: true
    };
    newPricing[plan].metadata.features.push(newFeature);
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleRemoveFeature = (plan: 'starter' | 'pro' | 'business', featureId: string) => {
    if (!planPricing) return;
    const newPricing = JSON.parse(JSON.stringify(planPricing));
    newPricing[plan].metadata.features = newPricing[plan].metadata.features.filter((f: PlanFeature) => f.id !== featureId);
    setPlanPricing(newPricing);
    saveToHistory(newPricing);
  };

  const handleSavePlanPricing = async () => {
    if (!planPricing) return;
    setIsSavingPricing(true);
    try {
      await planPricingService.updatePlanPricing(planPricing, currentUser?.email || 'admin');
      setOriginalPricing(JSON.parse(JSON.stringify(planPricing)));
      showSuccess('Settings saved successful');
    } catch (error) { showError('Failed to save'); } finally { setIsSavingPricing(false); }
  };

  const handleUndo = () => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current--;
      setPlanPricing(JSON.parse(JSON.stringify(historyRef.current[historyIndexRef.current])));
    }
  };

  const handleRedo = () => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current++;
      setPlanPricing(JSON.parse(JSON.stringify(historyRef.current[historyIndexRef.current])));
    }
  };

  const filteredTiers = () => {
    if (!planPricing || activeTab === 'starter') return [];
    const tiers = planPricing[activeTab].tiers;
    if (!searchTerm) return tiers;
    return tiers.filter(t => t.subscribers.toString().includes(searchTerm) || t.price.toString().includes(searchTerm));
  };

  const hasUnsavedChanges = JSON.stringify(planPricing) !== JSON.stringify(originalPricing);

  if (isLoadingPricing || !planPricing) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white min-h-screen">
        <div className="w-8 h-8 border-b-2 border-cyan-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-400 text-xs font-light uppercase tracking-widest">Loading Settings...</p>
      </div>
    );
  }

  const cardBaseStyles = `rounded-lg transition-colors p-6 shadow-md ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-sm'
    }`;

  return (
    <div className="max-w-7xl mx-auto space-y-4 animate-in fade-in duration-500 font-sans p-6 min-h-screen transition-colors">

      {/* Align with Admin Overview Header Area */}
      <div className={`${cardBaseStyles} flex flex-col md:flex-row items-center justify-between gap-4`}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <h1 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'}`}>Plan & Pricing Editor</h1>
            <p className={`text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage tiers, features, and dynamic metadata.</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex rounded-lg p-1 border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
            <button onClick={handleUndo} disabled={historyIndexRef.current <= 0} className="p-1.5 text-gray-400 hover:text-cyan-600 disabled:opacity-30 transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg></button>
            <button onClick={handleRedo} disabled={historyIndexRef.current >= historyRef.current.length - 1} className="p-1.5 text-gray-400 hover:text-cyan-600 disabled:opacity-30 transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" /></svg></button>
          </div>
          <button
            onClick={handleSavePlanPricing}
            disabled={isSavingPricing || validationErrors.length > 0}
            className={`px-6 py-2 rounded-lg text-xs font-medium uppercase tracking-wider transition-all shadow ${hasUnsavedChanges ? 'bg-cyan-600 text-white hover:bg-cyan-700 active:scale-95' : 'bg-gray-300 text-gray-400 cursor-default'
              }`}
          >
            {isSavingPricing ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start pb-8">
        {/* Left Toolbar & Meta Info */}
        <div className="lg:col-span-12 xl:col-span-4 space-y-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'} rounded-lg p-1 flex gap-1 shadow-sm`}>
            {(['starter', 'pro', 'business'] as const).map(p => (
              <button
                key={p} onClick={() => setActiveTab(p)}
                className={`flex-1 py-1.5 px-3 rounded text-[11px] font-medium uppercase tracking-wider transition-all ${activeTab === p ? (isDark ? 'bg-cyan-900/30 text-cyan-300' : 'bg-cyan-100 text-cyan-800 shadow-sm') : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')}`}
              >{p}</button>
            ))}
          </div>

          <div className={cardBaseStyles}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Basic Information</h3>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Popular</span>
                <button
                  onClick={() => handleMetadataChange(activeTab, 'isPopular', !planPricing[activeTab].metadata.isPopular)}
                  className={`w-8 h-4 rounded-full relative transition-colors ${planPricing[activeTab].metadata.isPopular ? 'bg-cyan-600' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${planPricing[activeTab].metadata.isPopular ? 'right-0.5' : 'left-0.5'}`}></div>
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-xs font-light mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Plan Description</label>
                <textarea
                  value={planPricing[activeTab].metadata.description}
                  onChange={(e) => handleMetadataChange(activeTab, 'description', e.target.value)}
                  className={`w-full ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 outline-none transition-all resize-none min-h-[80px] font-light`}
                />
              </div>

              <div>
                <label className={`block text-xs font-light mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>CTA Button Label</label>
                <input
                  type="text" value={planPricing[activeTab].metadata.ctaLabel || ''}
                  onChange={(e) => handleMetadataChange(activeTab, 'ctaLabel', e.target.value)}
                  className={`w-full ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 outline-none transition-all font-light`}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <label className={`block text-[10px] font-light uppercase tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Trial Period</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" value={planPricing[activeTab].metadata.trialDays}
                      onChange={(e) => handleMetadataChange(activeTab, 'trialDays', parseInt(e.target.value) || 0)}
                      className="bg-transparent text-sm font-medium text-cyan-600 w-full outline-none"
                    />
                    <span className="text-[10px] text-gray-400 font-light">DAYS</span>
                  </div>
                </div>
                {activeTab !== 'starter' && (
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <label className={`block text-[10px] font-light uppercase tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Yearly Disc.</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" value={planPricing[activeTab].metadata.billingCycles.yearlyDiscount}
                        onChange={(e) => handleMetadataChange(activeTab, 'billingCycles.yearlyDiscount', parseInt(e.target.value) || 0)}
                        className="bg-transparent text-sm font-medium text-emerald-600 w-full outline-none"
                      />
                      <span className="text-[10px] text-gray-400 font-light">%</span>
                    </div>
                  </div>
                )}
              </div>

              <div className={`p-4 rounded-lg border ${isDark ? 'bg-cyan-900/10 border-cyan-800/30' : 'bg-cyan-50 border-cyan-100'} space-y-3`}>
                <p className="text-[10px] font-medium text-cyan-600 uppercase tracking-widest">Billing Support</p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={planPricing[activeTab].metadata.billingCycles.monthly} onChange={(e) => handleMetadataChange(activeTab, 'billingCycles.monthly', e.target.checked)} className="hidden" />
                    <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${planPricing[activeTab].metadata.billingCycles.monthly ? 'bg-cyan-600 border-cyan-600' : 'bg-white border-gray-300'}`}>
                      {planPricing[activeTab].metadata.billingCycles.monthly && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={5} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <span className={`text-[11px] font-light ${planPricing[activeTab].metadata.billingCycles.monthly ? (isDark ? 'text-cyan-300' : 'text-cyan-900') : 'text-gray-400'}`}>MONTHLY</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={planPricing[activeTab].metadata.billingCycles.yearly} onChange={(e) => handleMetadataChange(activeTab, 'billingCycles.yearly', e.target.checked)} className="hidden" />
                    <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${planPricing[activeTab].metadata.billingCycles.yearly ? 'bg-cyan-600 border-cyan-600' : 'bg-white border-gray-300'}`}>
                      {planPricing[activeTab].metadata.billingCycles.yearly && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={5} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                    <span className={`text-[11px] font-light ${planPricing[activeTab].metadata.billingCycles.yearly ? (isDark ? 'text-cyan-300' : 'text-cyan-900') : 'text-gray-400'}`}>YEARLY</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className={cardBaseStyles}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Plan Quotas</h3>
            <div className="space-y-2">
              {[
                { id: 'contacts', label: 'Max Contacts', icon: '👥' },
                { id: 'emailsPerMonth', label: 'Email Quota', icon: '📧' },
                { id: 'workspaces', label: 'Workspaces', icon: '🏢' },
              ].map(limit => (
                <div key={limit.id} className={`flex items-center justify-between p-3 rounded border transition-all ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-white hover:border-cyan-300'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{limit.icon}</span>
                    <span className={`text-[11px] font-light ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{limit.label}</span>
                  </div>
                  <input
                    type="text" value={planPricing[activeTab].metadata.limits[limit.id as keyof typeof planPricing.starter.metadata.limits]}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase();
                      handleMetadataChange(activeTab, `limits.${limit.id}`, val === 'unlimited' ? 'unlimited' : (parseInt(val) || 0));
                    }}
                    className={`bg-transparent text-right text-sm font-medium w-24 outline-none ${isDark ? 'text-white' : 'text-gray-900'}`}
                    placeholder="Unlimited"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content: Feature Management & Pricing Tiers */}
        <div className="lg:col-span-12 xl:col-span-8 space-y-4">
          <div className={cardBaseStyles}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Feature Registry</h3>
              <button onClick={() => handleAddFeature(activeTab)} className="text-[11px] font-medium text-cyan-600 uppercase tracking-widest hover:text-cyan-700">+ Add Feature</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {planPricing[activeTab].metadata.features.map(f => (
                <div key={f.id} className={`flex flex-col p-4 rounded-lg border transition-all ${f.included ? (isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200') : 'bg-gray-50/50 border-transparent opacity-40'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <input
                      type="text" value={f.name} onChange={(e) => handleFeatureChange(activeTab, f.id, 'name', e.target.value)}
                      className={`bg-transparent text-sm font-medium outline-none w-full ${isDark ? 'text-white' : 'text-gray-900'}`}
                    />
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleRemoveFeature(activeTab, f.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                      <button
                        onClick={() => handleFeatureChange(activeTab, f.id, 'included', !f.included)}
                        className={`w-8 h-4 rounded-full relative transition-colors ${f.included ? 'bg-cyan-600' : 'bg-gray-300'}`}
                      >
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${f.included ? 'right-0.5' : 'left-0.5'}`}></div>
                      </button>
                    </div>
                  </div>
                  <input
                    type="text" placeholder="Detail value (Optional)" value={f.value || ''}
                    onChange={(e) => handleFeatureChange(activeTab, f.id, 'value', e.target.value)}
                    className={`bg-transparent border-b ${isDark ? 'border-gray-700' : 'border-gray-100'} text-xs font-light py-1 outline-none focus:border-cyan-500 transition-all ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={`${cardBaseStyles} min-h-[450px]`}>
            {activeTab === 'starter' ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center opacity-70">
                <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <h4 className={`text-sm font-medium uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>Starter Tier Fixed</h4>
                <p className={`text-xs font-light max-w-xs mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>The Starter plan is configured as a $0 base-point. Use the left panel to update its quotas and core features.</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                  <div className={`relative flex-1 w-full flex items-center gap-3 px-4 py-2 border rounded-lg transition-all ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-300'}`}>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input
                      type="text" placeholder="Search tiers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-transparent text-sm font-light outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setBulkEditPlan(activeTab as any); setShowBulkEdit(true); }} className={`px-4 py-2 text-[11px] font-medium uppercase tracking-wider rounded border transition-all ${isDark ? 'border-gray-600 text-gray-400 hover:text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Bulk Edit</button>
                    <button onClick={() => handleAddPricingTier(activeTab as any)} className="px-4 py-2 bg-cyan-600 text-white text-[11px] font-medium uppercase tracking-widest rounded shadow-sm hover:bg-cyan-700 active:scale-95 transition-all">+ Add Pricing Point</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {filteredTiers().map((tier, idx) => (
                    <div key={idx} className={`p-5 rounded-lg border transition-all group relative ${isDark ? 'bg-gray-900 border-gray-700 hover:border-cyan-700' : 'bg-white border-gray-200 hover:border-cyan-300 hover:shadow-cyan-100/50 hover:shadow-lg'}`}>
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">Entry {idx + 1}</span>
                        <button onClick={() => handleRemovePricingTier(activeTab as any, idx)} className="opacity-0 group-hover:opacity-100 p-1.5 rounded transition-all hover:bg-red-50 hover:text-red-500 text-gray-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className={`text-[10px] font-light uppercase tracking-wider mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Contact Limit</label>
                          <input
                            type="number" value={tier.subscribers}
                            onChange={(e) => handlePricingTierChange(activeTab as any, idx, 'subscribers', parseInt(e.target.value) || 0)}
                            className={`w-full bg-transparent border ${isDark ? 'border-gray-700' : 'border-gray-100'} rounded px-3 py-1.5 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} focus:border-cyan-500 outline-none`}
                          />
                        </div>
                        <div>
                          <label className={`text-[10px] font-light uppercase tracking-wider mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Monthly Cost ($)</label>
                          <input
                            type="number" value={tier.price}
                            onChange={(e) => handlePricingTierChange(activeTab as any, idx, 'price', parseFloat(e.target.value) || 0)}
                            className={`w-full bg-transparent border ${isDark ? 'border-gray-700' : 'border-gray-100'} rounded px-3 py-1.5 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} focus:border-cyan-500 outline-none`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showBulkEdit && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg p-8 max-w-sm w-full shadow-2xl`}>
            <h2 className={`text-lg font-light mb-2 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>System Wide Update</h2>
            <p className={`text-xs font-light text-center mb-6 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Modify all pricing points for <span className="font-medium text-cyan-600 uppercase">{bulkEditPlan}</span> simultaneously.</p>

            <div className="space-y-6">
              <div className={`grid grid-cols-2 gap-2 p-1 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                {(['percentage', 'fixed'] as const).map(t => (
                  <button key={t} onClick={() => setBulkEditType(t)} className={`py-1.5 rounded text-[11px] font-medium uppercase tracking-widest transition-all ${bulkEditType === t ? (isDark ? 'bg-gray-700 text-white shadow' : 'bg-white text-gray-900 shadow-sm') : 'text-gray-400'}`}>{t}</button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-400 text-xs font-medium">{bulkEditType === 'percentage' ? '%' : '$'}</span>
                <input
                  type="number" value={bulkEditValue} onChange={(e) => setBulkEditValue(parseFloat(e.target.value) || 0)}
                  className={`w-full ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} border rounded px-5 py-4 pl-8 text-sm font-medium outline-none focus:ring-2 focus:ring-cyan-500`}
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button onClick={() => setShowBulkEdit(false)} className={`flex-1 py-3 text-[11px] font-medium uppercase tracking-widest transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>Cancel</button>
                <button onClick={handleBulkEdit} className="flex-1 py-3 rounded bg-cyan-600 text-white text-[11px] font-medium uppercase tracking-widest hover:bg-cyan-700 transition-all shadow active:scale-95">Apply Change</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlanPricing;
